import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, FileText, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import EnergyDiagram from "@/components/EnergyDiagram";
import CTASection from "@/components/CTASection";
import { useSite, useBrand } from "@/context/SiteContext";
import { useSeo } from "@/lib/seo";
import { SOLUTIONS, brandify } from "@/data/content";

export default function SolutionDetail() {
  const { slug } = useParams();
  const s = SOLUTIONS.find((x) => x.slug === slug);
  const { openQuote, waLink } = useSite();
  const brand = useBrand();
  const intro = brandify(s?.intro, brand.name);
  useSeo({ title: s ? `${s.name} | ${brand.name}` : "Solution", description: s ? `${s.hero}. ${intro?.slice(0, 150)}` : "", canonical: `https://${brand.domain}/solutions/${slug}` });

  if (!s) return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-slate-300">Solution not found. <Link to="/solutions" className="text-emerald-400">Back</Link></div>;

  return (
    <div>
      <PageHeader label={s.sector} title={s.hero} sub={s.tagline || s.intro?.slice(0, 120)} image={s.image} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <Reveal>
            <Link to="/solutions" className="text-emerald-400 text-sm inline-flex items-center gap-1 mb-4"><ArrowLeft size={15} /> All Solutions</Link>
            <p className="text-slate-300 leading-relaxed text-lg">{intro}</p>
          </Reveal>

          {s.stages && (
            <Stagger className="grid sm:grid-cols-2 gap-5">
              {s.stages.map((st, i) => (
                <motion.div key={st.title} variants={staggerItem} className="card-surface p-6">
                  <div className="font-mono text-emerald-400 text-sm">{String(i + 1).padStart(2, "0")}</div>
                  <div className="font-display font-semibold text-white text-lg mt-1 mb-3">{st.title}</div>
                  <ul className="space-y-1.5">{st.items.map((it) => <li key={it} className="text-sm text-slate-400">— {it}</li>)}</ul>
                </motion.div>
              ))}
            </Stagger>
          )}

          {s.process && (
            <Reveal><div className="flex flex-wrap items-center gap-3">
              {s.process.map((p, i) => (
                <div key={p} className="flex items-center gap-3">
                  <span className="badge-terminal !text-sm">{p}</span>
                  {i < s.process.length - 1 && <ArrowRight size={16} className="text-emerald-500" />}
                </div>
              ))}
            </div></Reveal>
          )}

          {s.diagram && <Reveal className="card-surface p-6"><div className="mono-label mb-3">System Flow</div><EnergyDiagram /></Reveal>}

          {s.applications && (
            <Reveal>
              <h2 className="font-display text-2xl font-bold text-white mb-4">{s.slug === "government-institutional" ? "Supply Categories" : "Applications"}</h2>
              <div className="flex flex-wrap gap-2">{s.applications.map((a) => <span key={a} className="badge-terminal">{a}</span>)}</div>
            </Reveal>
          )}

          {s.note && (
            <div className="card-surface p-5 border-amber-500/30 !bg-amber-500/5">
              <div className="mono-label !text-amber-400">Note</div>
              <p className="text-sm text-slate-300 mt-1">{s.note}</p>
            </div>
          )}
        </div>

        <aside>
          <div className="card-surface p-6 sticky top-28">
            <div className="mono-label">Get Started</div>
            <h3 className="font-display text-xl font-bold text-white mt-1 mb-4">{s.cta || "Discuss Your Requirement"}</h3>
            <button onClick={() => openQuote(s.name)} className="btn-primary w-full mb-3" data-testid="solution-quote-button"><FileText size={16} /> {s.cta || "Get a Quote"}</button>
            <a href={waLink(`Hello ${brand.name}, I'm interested in ${s.name}.`)} target="_blank" rel="noreferrer" className="btn-ghost w-full" data-testid="solution-whatsapp-button"><MessageCircle size={16} /> WhatsApp Us</a>
          </div>
        </aside>
      </section>
      <CTASection />
    </div>
  );
}
